// Página pública do link de indicação (pedido 2026-09-27) — /indicacao/:code,
// sem login (bypass em __root.tsx, igual /ao-vivo). A pessoa indicada deixa
// só nome + WhatsApp; o backend recusa duplicado, autoindicação e quem já é
// cliente. Nunca mostra dados de quem indicou além do primeiro nome.
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { CheckCircle2, Gift, Loader2, ShieldCheck } from "lucide-react";
import defaultLogoUrl from "@/assets/gps-drive-logo-v3.png";
import { GpsDriveApiError, resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";
import {
  gpsDriveReferralsService,
  type PublicReferralInfo,
} from "@/services/gpsdrive-referrals.service";
import { gpsDriveAdminBrandingService } from "@/services/gpsdrive-admin-branding.service";

export const Route = createFileRoute("/indicacao/$code")({
  head: () => ({
    meta: [
      { title: "Você foi indicado — GPS Drive" },
      { property: "og:title", content: "Você foi indicado para o GPS Drive" },
      {
        property: "og:description",
        content: "Rastreamento veicular em tempo real. Deixe seu contato e nossa equipe fala com você.",
      },
      { name: "theme-color", content: "#081534" },
    ],
  }),
  component: ReferralLandingPage,
});

const VISIT_KEY = (code: string) => `gpsdrive:referral-visit:${code}`;

function maskPhone(value: string) {
  const d = value.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function ReferralLandingPage() {
  const { code } = Route.useParams();
  const [info, setInfo] = useState<PublicReferralInfo | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ok" | "invalid" | "error">("loading");
  const [logoUrl, setLogoUrl] = useState<string>(defaultLogoUrl);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    gpsDriveReferralsService
      .publicInfo(code)
      .then((r) => {
        if (cancelled) return;
        setInfo(r);
        setLoadState("ok");
        // Uma visita por aparelho/link: reaproveita o token já guardado.
        let saved: string | null = null;
        try {
          saved = localStorage.getItem(VISIT_KEY(code));
        } catch {
          // sem localStorage — segue sem "Link acessado"
        }
        if (!saved) {
          gpsDriveReferralsService
            .registerVisit(code)
            .then(({ visitToken }) => {
              try {
                localStorage.setItem(VISIT_KEY(code), visitToken);
              } catch {
                // idem
              }
            })
            .catch(() => undefined);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadState(e instanceof GpsDriveApiError && e.status === 404 ? "invalid" : "error");
      });
    gpsDriveAdminBrandingService
      .getPublicLoginBranding()
      .then((b) => {
        if (!cancelled && b.loginLogoUrl) setLogoUrl(resolveGpsDriveUrl(b.loginLogoUrl));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [code]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length < 2) {
      setError("Informe seu nome");
      return;
    }
    if (phone.replace(/\D/g, "").length < 10) {
      setError("Informe seu WhatsApp com DDD");
      return;
    }
    setSubmitting(true);
    try {
      let visitToken: string | undefined;
      try {
        visitToken = localStorage.getItem(VISIT_KEY(code)) ?? undefined;
      } catch {
        visitToken = undefined;
      }
      await gpsDriveReferralsService.register(code, { name: name.trim(), phone, visitToken });
      setDone(true);
    } catch (err) {
      setError(
        err instanceof GpsDriveApiError
          ? err.status === 429
            ? "Muitas tentativas. Aguarde um minuto e tente de novo."
            : err.message
          : "Não foi possível enviar. Verifique sua conexão.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-dvh bg-slate-50 text-slate-900">
      <div className="relative overflow-hidden rounded-b-[36px] bg-gradient-to-br from-[#081534] via-[#0f2a66] to-[#1d4ed8] px-5 pb-20 pt-8 text-white">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/5" />
        <div className="mx-auto max-w-md">
          <img src={logoUrl} alt="Logotipo" className="h-10 w-auto object-contain" />
          <span className="mt-6 inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
            <Gift className="h-3.5 w-3.5" /> Indicação
          </span>
          <h1 className="mt-3 text-[28px] font-extrabold leading-tight">
            {info ? `${info.referrerFirstName} indicou você!` : "Você foi indicado!"}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-blue-100">
            Rastreamento do seu veículo em tempo real, direto no celular. Deixe seu contato e a
            equipe {info?.companyName ? `da ${info.companyName} ` : ""}fala com você pelo WhatsApp.
          </p>
        </div>
      </div>

      <div className="relative mx-auto -mt-12 max-w-md px-4 pb-10">
        <div className="rounded-3xl bg-white p-5 shadow-xl shadow-slate-900/10">
          {loadState === "loading" ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" aria-label="Carregando" />
            </div>
          ) : loadState === "invalid" ? (
            <p className="py-8 text-center text-sm text-slate-600" role="alert">
              Este link de indicação não é válido ou não está mais ativo.
            </p>
          ) : loadState === "error" ? (
            <p className="py-8 text-center text-sm text-slate-600" role="alert">
              Não foi possível abrir o link agora. Tente de novo em instantes.
            </p>
          ) : done ? (
            <div className="flex flex-col items-center py-6 text-center" role="status">
              <CheckCircle2 className="h-14 w-14 text-emerald-500" />
              <h2 className="mt-3 text-lg font-bold">Cadastro recebido!</h2>
              <p className="mt-1 text-sm text-slate-600">
                Em breve nossa equipe entra em contato pelo seu WhatsApp.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-4">
              <h2 className="text-base font-bold">Quero ser contatado</h2>
              <div className="space-y-1.5">
                <label htmlFor="ref-name" className="text-sm font-medium text-slate-700">
                  Seu nome
                </label>
                <input
                  id="ref-name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-[15px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="ref-phone" className="text-sm font-medium text-slate-700">
                  WhatsApp com DDD
                </label>
                <input
                  id="ref-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="(11) 99999-9999"
                  value={phone}
                  onChange={(e) => setPhone(maskPhone(e.target.value))}
                  className="h-12 w-full rounded-2xl border border-slate-200 px-4 text-[15px] outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              {error && (
                <p className="text-sm text-rose-600" role="alert">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-blue-600 text-[15px] font-bold text-white shadow-lg shadow-blue-600/25 active:bg-blue-700 disabled:opacity-70"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? "Enviando…" : "Enviar"}
              </button>
              <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
                <ShieldCheck className="mt-px h-3.5 w-3.5 shrink-0" />
                Usamos seus dados só para entrar em contato sobre o serviço. Quem indicou você vê
                apenas seu primeiro nome, a inicial do sobrenome e a etapa do atendimento.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
